import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, unref as _unref, openBlock as _openBlock, createElementBlock as _createElementBlock, createTextVNode as _createTextVNode, withCtx as _withCtx, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createVNode as _createVNode, renderList as _renderList, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "line-config" }
const _hoisted_2 = { class: "line-panel" }
const _hoisted_3 = { class: "panel-header" }
const _hoisted_4 = { class: "line-list" }
const _hoisted_5 = {
  key: 0,
  class: "line-item is-current"
}
const _hoisted_6 = { class: "current-info" }
const _hoisted_7 = { class: "current-name-row" }
const _hoisted_8 = { class: "current-name" }
const _hoisted_9 = {
  key: 0,
  class: "current-host"
}
const _hoisted_10 = {
  key: 0,
  class: "current-actions"
}
const _hoisted_11 = { class: "line-body" }
const _hoisted_12 = { class: "line-name" }
const _hoisted_13 = {
  key: 0,
  class: "line-host"
}
const _hoisted_14 = { class: "line-actions" }
const _hoisted_15 = {
  key: 1,
  class: "empty-tip"
}
const _hoisted_16 = { class: "modal-form" }
const _hoisted_17 = { class: "form-item" }
const _hoisted_18 = { class: "form-item" }
const _hoisted_19 = {
  key: 0,
  class: "form-item"
}

import { ref, computed, onMounted, inject, type Ref } from 'vue';
import {
  NButton,
  NInput,
  NSwitch,
  NModal,
  NPopconfirm,
} from 'naive-ui';
import Tools from '@/ts/Tools';

// ========== 线路列表 ==========
interface LineItem {
  id?: number;
  name: string;
  host: string;
  sort_order: number;
  is_current: number;
  create_time?: number;
  update_time?: number;
}


export default /*@__PURE__*/_defineComponent({
  __name: 'LineConfigManage',
  setup(__props) {

const lineList = ref<LineItem[]>([]);

// 调试模式：Ctrl+Shift+D 统一切换线路地址显示
const debugMode = inject<Ref<boolean>>('debugMode', ref(false));

// 当前使用的线路
const currentLine = computed(() =>
  lineList.value.find((item) => item.is_current == 1) ?? null
);

// 其他线路（排除当前使用）
const otherLines = computed(() =>
  lineList.value.filter((item) => item.is_current != 1)
);

// ========== 弹窗 ==========
const showModal = ref(false);
const editingId = ref<number | null>(null);
const submitting = ref(false);

const modalTitle = ref('新增线路');
const form = ref({
  name: '',
  host: '',
  sortOrder: 0,
  isCurrent: false,
});

// ========== 加载线路列表 ==========
async function loadLineList() {
  Tools.FetchGetAsync<{ total: number; list: LineItem[] }>(
    'line/list', {}
  ).then((resp) => {
    if (resp.CheckIsOk() && resp.Data) {
      lineList.value = resp.Data.list ?? [];
    }
  });
}

// ========== 打开新增弹窗 ==========
function openAddModal() {
  editingId.value = null;
  modalTitle.value = '新增线路';
  form.value = { name: '', host: '', sortOrder: 0, isCurrent: false };
  showModal.value = true;
}

// ========== 打开编辑弹窗 ==========
function openEditModal(item: LineItem) {
  editingId.value = item.id!;
  modalTitle.value = '编辑线路';
  form.value = {
    name: item.name,
    host: item.host,
    sortOrder: item.sort_order,
    isCurrent: false, // 编辑不传此字段，占位用
  };
  showModal.value = true;
}

// ========== 提交表单 ==========
async function handleSubmit() {
  // 防重复提交
  if (submitting.value) return false;
  submitting.value = true;

  try {
    // 去除首尾空格
    form.value.name = form.value.name.trim();
    form.value.host = form.value.host.trim();

    if (!form.value.name) {
      Tools.AlertError('线路名称不能为空');
      return false;
    }
    if (/\s/.test(form.value.name)) {
      Tools.AlertError('线路名称不能包含空格');
      return false;
    }
    if (!form.value.host) {
      Tools.AlertError('线路域名不能为空');
      return false;
    }
    if (/\s/.test(form.value.host)) {
      Tools.AlertError('线路域名不能包含空格');
      return false;
    }

    if (editingId.value) {
      // 编辑
      const resp = await Tools.FetchAsync<{ id: number }>(
        'line/update',
        {
          id: editingId.value,
          name: form.value.name,
          host: form.value.host,
          sortOrder: form.value.sortOrder,
        },
        'POST'
      );
      if (resp.CheckIsOk()) {
        Tools.AlertSuccess('更新成功');
        showModal.value = false;
        refreshAll();
      }
    } else {
      // 新增
      const resp = await Tools.FetchAsync<{ name: string; host: string }>(
        'line/create',
        {
          name: form.value.name,
          host: form.value.host,
          sortOrder: form.value.sortOrder,
          isCurrent: form.value.isCurrent ? 1 : 0,
        },
        'POST'
      );
      if (resp.CheckIsOk()) {
        Tools.AlertSuccess('新增成功');
        showModal.value = false;
        refreshAll();
      }
    }
  } finally {
    submitting.value = false;
  }
}

// ========== 删除线路 ==========
async function handleDeleteLine(id: number) {
  Tools.FetchGetAsync<{ id: number; wasCurrent: boolean }>(
    'line/delete', { id }
  ).then((resp) => {
    if (resp.CheckIsOk()) {
      Tools.AlertSuccess('删除成功');
      refreshAll();
    }
  });
}

// ========== 选择当前线路 ==========
async function handleSelectLine(item: LineItem) {
  Tools.FetchAsync<{ id: number; name: string; host: string }>(
    'line/select',
    { id: item.id! },
    'POST'
  ).then((resp) => {
    if (resp.CheckIsOk()) {
      Tools.AlertSuccess(`已切换到线路: ${item.name}`);
      refreshAll();
    }
  });
}

// ========== 刷新 ==========
function refreshAll() {
  loadLineList();
}

onMounted(() => {
  refreshAll();
});

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", _hoisted_2, [
      _createElementVNode("div", _hoisted_3, [
        _cache[9] || (_cache[9] = _createElementVNode("span", { class: "panel-title" }, "线路管理", -1)),
        (_unref(debugMode))
          ? (_openBlock(), _createBlock(_unref(NButton), {
              key: 0,
              type: "primary",
              size: "large",
              onClick: _cache[0] || (_cache[0] = ($event: any) => (openAddModal())),
              class: "add-btn"
            }, {
              icon: _withCtx(() => _cache[7] || (_cache[7] = [
                _createElementVNode("svg", {
                  xmlns: "http://www.w3.org/2000/svg",
                  width: "18",
                  height: "18",
                  viewBox: "0 0 24 24",
                  fill: "none",
                  stroke: "currentColor",
                  "stroke-width": "2.5",
                  "stroke-linecap": "round",
                  "stroke-linejoin": "round"
                }, [
                  _createElementVNode("line", {
                    x1: "12",
                    y1: "5",
                    x2: "12",
                    y2: "19"
                  }),
                  _createElementVNode("line", {
                    x1: "5",
                    y1: "12",
                    x2: "19",
                    y2: "12"
                  })
                ], -1)
              ])),
              default: _withCtx(() => [
                _cache[8] || (_cache[8] = _createTextVNode(" 新增 "))
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _createElementVNode("div", _hoisted_4, [
        (currentLine.value)
          ? (_openBlock(), _createElementBlock("div", _hoisted_5, [
              _createElementVNode("div", _hoisted_6, [
                _createElementVNode("div", _hoisted_7, [
                  _cache[10] || (_cache[10] = _createElementVNode("span", { class: "current-badge" }, [
                    _createElementVNode("svg", {
                      xmlns: "http://www.w3.org/2000/svg",
                      width: "12",
                      height: "12",
                      viewBox: "0 0 24 24",
                      fill: "none",
                      stroke: "currentColor",
                      "stroke-width": "3",
                      "stroke-linecap": "round",
                      "stroke-linejoin": "round"
                    }, [
                      _createElementVNode("polyline", { points: "20 6 9 17 4 12" })
                    ]),
                    _createTextVNode(" 当前使用 ")
                  ], -1)),
                  _createElementVNode("span", _hoisted_8, _toDisplayString(currentLine.value.name), 1)
                ]),
                (_unref(debugMode))
                  ? (_openBlock(), _createElementBlock("span", _hoisted_9, _toDisplayString(currentLine.value.host), 1))
                  : _createCommentVNode("", true),
                _cache[11] || (_cache[11] = _createElementVNode("span", { class: "current-desc" }, "当前 App 请求将通过此线路转发", -1))
              ]),
              (_unref(debugMode))
                ? (_openBlock(), _createElementBlock("div", _hoisted_10, [
                    _createVNode(_unref(NButton), {
                      size: "medium",
                      type: "warning",
                      ghost: "",
                      onClick: _cache[1] || (_cache[1] = ($event: any) => (openEditModal(currentLine.value))),
                      class: "action-btn"
                    }, {
                      default: _withCtx(() => _cache[12] || (_cache[12] = [
                        _createTextVNode("修改")
                      ])),
                      _: 1
                    })
                  ]))
                : _createCommentVNode("", true),
              _cache[13] || (_cache[13] = _createElementVNode("div", { class: "current-mask" }, null, -1))
            ]))
          : _createCommentVNode("", true),
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(otherLines.value, (item) => {
          return (_openBlock(), _createElementBlock("div", {
            class: "line-item",
            key: item.id
          }, [
            _createElementVNode("div", _hoisted_11, [
              _createElementVNode("span", _hoisted_12, _toDisplayString(item.name), 1),
              (_unref(debugMode))
                ? (_openBlock(), _createElementBlock("span", _hoisted_13, _toDisplayString(item.host), 1))
                : _createCommentVNode("", true)
            ]),
            _createElementVNode("div", _hoisted_14, [
              _createVNode(_unref(NButton), {
                size: "medium",
                type: "info",
                ghost: "",
                onClick: ($event: any) => (handleSelectLine(item)),
                class: "action-btn"
              }, {
                default: _withCtx(() => _cache[14] || (_cache[14] = [
                  _createTextVNode(" 选用 ")
                ])),
                _: 2
              }, 1032, ["onClick"]),
              (_unref(debugMode))
                ? (_openBlock(), _createBlock(_unref(NButton), {
                    key: 0,
                    size: "medium",
                    type: "warning",
                    ghost: "",
                    onClick: ($event: any) => (openEditModal(item)),
                    class: "action-btn"
                  }, {
                    default: _withCtx(() => _cache[15] || (_cache[15] = [
                      _createTextVNode(" 编辑 ")
                    ])),
                    _: 2
                  }, 1032, ["onClick"]))
                : _createCommentVNode("", true),
              (_unref(debugMode))
                ? (_openBlock(), _createBlock(_unref(NPopconfirm), {
                    key: 1,
                    onPositiveClick: ($event: any) => (handleDeleteLine(item.id!))
                  }, {
                    trigger: _withCtx(() => [
                      _createVNode(_unref(NButton), {
                        size: "medium",
                        type: "error",
                        ghost: "",
                        class: "action-btn"
                      }, {
                        default: _withCtx(() => _cache[16] || (_cache[16] = [
                          _createTextVNode("删除")
                        ])),
                        _: 1
                      })
                    ]),
                    default: _withCtx(() => [
                      _createTextVNode(" 确定删除线路 \"" + _toDisplayString(item.name) + "\" 吗？ ", 1)
                    ]),
                    _: 2
                  }, 1032, ["onPositiveClick"]))
                : _createCommentVNode("", true)
            ])
          ]))
        }), 128)),
        (lineList.value.length === 0)
          ? (_openBlock(), _createElementBlock("div", _hoisted_15, "暂无线路配置，点击「新增」开始添加"))
          : _createCommentVNode("", true)
      ])
    ]),
    _createVNode(_unref(NModal), {
      show: showModal.value,
      "onUpdate:show": _cache[5] || (_cache[5] = ($event: any) => ((showModal).value = $event)),
      preset: "dialog",
      title: modalTitle.value,
      "positive-text": "确认",
      "negative-text": "取消",
      loading: submitting.value,
      onPositiveClick: handleSubmit,
      onNegativeClick: _cache[6] || (_cache[6] = ($event: any) => {showModal.value = false; submitting.value = false})
    }, {
      default: _withCtx(() => [
        _createElementVNode("div", _hoisted_16, [
          _createElementVNode("div", _hoisted_17, [
            _cache[17] || (_cache[17] = _createElementVNode("span", { class: "form-label" }, "线路名称", -1)),
            _createVNode(_unref(NInput), {
              value: form.value.name,
              "onUpdate:value": _cache[2] || (_cache[2] = ($event: any) => ((form.value.name) = $event)),
              placeholder: "如：加速、直连、本地",
              size: "large"
            }, null, 8, ["value"])
          ]),
          _createElementVNode("div", _hoisted_18, [
            _cache[18] || (_cache[18] = _createElementVNode("span", { class: "form-label" }, "线路域名", -1)),
            _createVNode(_unref(NInput), {
              value: form.value.host,
              "onUpdate:value": _cache[3] || (_cache[3] = ($event: any) => ((form.value.host) = $event)),
              placeholder: "如：api.example.com",
              size: "large"
            }, null, 8, ["value"])
          ]),
          (!editingId.value)
            ? (_openBlock(), _createElementBlock("div", _hoisted_19, [
                _cache[19] || (_cache[19] = _createElementVNode("span", { class: "form-label" }, "设为当前线路", -1)),
                _createVNode(_unref(NSwitch), {
                  value: form.value.isCurrent,
                  "onUpdate:value": _cache[4] || (_cache[4] = ($event: any) => ((form.value.isCurrent) = $event)),
                  size: "large"
                }, null, 8, ["value"])
              ]))
            : _createCommentVNode("", true)
        ])
      ]),
      _: 1
    }, 8, ["show", "title", "loading"])
  ]))
}
}

})